import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import { Card, Tooltip, useMediaQuery } from "@mui/material";
import {
  ComputerCPUIcon,
  ComputerGPUIcon,
  ComputerHDDIcon, ComputerKeyboardIcon, ComputerLaptopIcon,
  ComputerRAMIcon,
  OilGasMeterIcon, StackLayersModernWorkplaceIcon
} from "component/commonComponents/Icon";
import React, { useContext } from "react";
import { NOT_APPLICABLE } from "../../../../constants/originalState";
import { StyledCurrentDevice } from "./currentDevice.style";
import { ThemeContext } from "styled-components";

export interface ICurrentDeviceProps {
  device?: any;
  title: string;
  isBackground?: boolean;
  locale?: any;
  isPickup?: boolean;
  isSpecification?: boolean;
};

export default function CurrentDevice(props: ICurrentDeviceProps) {
  const { device, title, locale, isBackground, isPickup, isSpecification } = props;
  const maxLength = 45;
  const theme = useContext(ThemeContext);
  const xsUp = useMediaQuery(`(min-width:${theme.breakpoints.xs})`);
  const smUp = useMediaQuery(`(min-width:${theme.breakpoints.sm})`);
  const mobileView = { xsUp, smUp };

  const createKeyboardLanguage = () => {
    let getKeyboardName = '';

    device?.currentDevice?.keyboards?.length > 0 && device?.currentDevice?.keyboards.forEach((keyboard: any) => {
      if (keyboard.code === device?.currentDevice?.keyboardSelected) {
        getKeyboardName = keyboard.name;
      }
    });
    return getKeyboardName;
  };

  const keyboardLanguage = createKeyboardLanguage() || NOT_APPLICABLE;

  const weightMeasure = (uid: any) => {
    if (
      !!device.dataMeasureCurrentDevice &&
      device.dataMeasureCurrentDevice.measure.uid === uid
    ) {
      return device?.dataMeasureCurrentDevice?.measure.weight;
    }
  };

  const sizeMeasure = (uid: any) => {
    if (
      !!device.dataMeasureCurrentDevice &&
      device.dataMeasureCurrentDevice.measure.uid === uid
    ) {
      return device?.dataMeasureCurrentDevice?.measure.size;
    }
  };

  const handleShowDeviceInfo = (value: string | null) => {
    if (value === 'null') return NOT_APPLICABLE;
    return value || NOT_APPLICABLE;
  };

  return (
    <StyledCurrentDevice mobileView={mobileView}>
      <div className="title">{title}</div>
      <Card className={`current-device-container ${isBackground? "background-default" : "background-empty"}`}>
        <div className={"device-image-container"}>
          <img
            src={device?.currentDevice?.model_picture || device?.currentDevice?.picture}
            alt="my current device"
            title="my current device"
            id="img-model_picture"
          />
        </div>
        <div className="device-spec-container">
          <div className="device-model">
            <span className="manufacturer" id="lbl-currentdevice-manufacturer">
              {device?.currentDevice?.manufacturer}
            </span>
            <span className="model" id="lbl-currentdevice-model">{device?.currentDevice?.model || device?.currentDevice?.user_device_profile}</span>
          </div>

          {isSpecification ? (
            <>
              <span className="specification">{locale?.specification}</span>
              <div className="specification-content columns-3">
                <div className="spec-details">
                  <Tooltip
                    title={locale?.tooltip.processor}
                    arrow
                    placement="left"
                  >
                    <ComputerCPUIcon
                      role="img"
                      aria-label={locale?.tooltip.cpu}
                      className="spec-icon"
                    />
                  </Tooltip>

                  <Tooltip title={`${device?.currentDevice?.model_processor?.length > maxLength ? device?.currentDevice?.model_processor : ''}`} arrow placement="top-start">
                    <div className="spec-value" id="lbl-currentdevice-model_processor">
                      {handleShowDeviceInfo(device?.currentDevice?.model_processor)}
                    </div>
                  </Tooltip>
                </div>

                <div className="spec-details">
                  <Tooltip
                    title={locale?.tooltip.graphicsProcessor}
                    arrow
                    placement="left"
                  >
                    <ComputerGPUIcon
                      role="img"
                      aria-label={locale?.tooltip.gpu}
                      className="spec-icon"
                    />
                  </Tooltip>
                  
                  <Tooltip title={`${device?.currentDevice?.model_graphics_processor?.length > maxLength ? device?.currentDevice?.model_graphics_processor : ''}`} arrow placement="top-start">
                    <div className="spec-value" id="lbl-currentdevice-model_graphics_processor">
                      {handleShowDeviceInfo(device?.currentDevice?.model_graphics_processor)}
                    </div>
                  </Tooltip>
                </div>

                <div className="spec-details">
                  <Tooltip title={locale?.tooltip.memory} arrow placement="left">
                  <ComputerRAMIcon
                      role="img"
                      aria-label={locale?.tooltip.ram}
                      className="spec-icon"
                    />
                  </Tooltip>

                  <Tooltip title={`${device?.currentDevice?.model_memory?.length > maxLength ? device?.currentDevice?.model_memory : ''}`} arrow placement="top-start">
                    <div className="spec-value" id="lbl-currentdevice-model_memory">
                      {handleShowDeviceInfo(device?.currentDevice?.model_memory)}
                    </div>
                  </Tooltip>
                </div>

                <div className="spec-details">
                  <Tooltip
                    title={locale?.tooltip.hardDrive}
                    arrow
                    placement="left"
                  >
                    <ComputerHDDIcon
                      role="img"
                      aria-label={locale?.tooltip.hdd}
                      className="spec-icon"
                    />
                  </Tooltip>

                  <Tooltip title={`${device?.currentDevice?.model_hard_drive?.length > maxLength ? device?.currentDevice?.model_hard_drive : ''}`} arrow placement="top-start">
                    <div className="spec-value" id="lbl-currentdevice-model_hard_drive">
                      {handleShowDeviceInfo(device?.currentDevice?.model_hard_drive)}
                    </div>
                  </Tooltip>
                </div>

                <div className="spec-details">
                  <Tooltip
                    title={locale?.tooltip.display}
                    arrow
                    placement="left"
                  >
                    <ComputerLaptopIcon
                      role="img"
                      aria-label={locale?.tooltip.screen}
                      className="spec-icon"
                    />
                  </Tooltip>

                  <Tooltip title={`${sizeMeasure(device?.currentDevice?.uid)?.length > maxLength ? sizeMeasure(device?.currentDevice?.uid) : ''}`} arrow placement="top-start">
                    <div className="spec-value" id="lbl-currentdevice-size">
                      {handleShowDeviceInfo(sizeMeasure(device?.currentDevice?.uid))}
                    </div>
                  </Tooltip>
                </div>

                <div className="spec-details">
                  <Tooltip title={locale?.tooltip.weight} arrow placement="left">
                  <OilGasMeterIcon
                      role="img"
                      aria-label={locale?.tooltip.weight}
                      className="spec-icon"
                    />
                  </Tooltip>
                  <div className="spec-value" id="lbl-currentdevice-weight">
                    {handleShowDeviceInfo(weightMeasure(device?.currentDevice?.uid))}
                  </div>
                </div>

                <div className="spec-details">
                  <Tooltip
                    title={locale?.tooltip.keyboardLayout}
                    arrow
                    placement="left"
                  >
                    <ComputerKeyboardIcon
                      role="img"
                      aria-label={locale?.tooltip.keyboardLayout}
                      className="spec-icon"
                    />
                  </Tooltip>
                  <div className="spec-value" id={isPickup ? "lbl-currentdevice-keyboard-name" : "lbl-currentdevice-model_language"}>
                    {isPickup ? 
                      (handleShowDeviceInfo(device?.currentDevice?.keyboard?.name)) 
                    :
                      (handleShowDeviceInfo(device?.currentDevice?.model_language))
                    }
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className={`specification-content columns-2 ${isPickup ? 'show-keyboard' : ''}`}>
              <div className="spec-details">
                <Tooltip
                  title={locale?.tooltip.serialNumber}
                  arrow
                  placement="left"
                >
                  <LocalOfferOutlinedIcon className="spec-icon" role="img" aria-label="Current device serial" />
                </Tooltip>

                <Tooltip title={`${device?.currentDevice?.serial_number?.length > maxLength ? device?.currentDevice?.serial_number : ''}`} arrow placement="top-start">
                  <div className="spec-value" id="lbl-currentdevice-serial_number">
                    {handleShowDeviceInfo(device?.currentDevice?.serial_number)}
                  </div>
                </Tooltip>
              </div>

              {isPickup && <div className="spec-details">
                <Tooltip
                  title={locale.tooltip.keyboardLayout}
                  arrow
                  placement="left"
                >
                  <ComputerKeyboardIcon
                    role="img"
                    aria-label={locale.tooltip.keyboardLayout}
                    className="spec-icon"
                  />
                </Tooltip>
                <div className="spec-value" id="lbl-currentdevice-keyboard-name">{keyboardLanguage}</div>
              </div>}

              <div className="spec-details">
                <Tooltip
                  title={locale?.tooltip.modelType}
                  arrow
                  placement="left"
                >
                  <StackLayersModernWorkplaceIcon
                    role="img"
                    aria-label={locale?.tooltip.modelType}
                    className="spec-icon"
                  />
                </Tooltip>

                <Tooltip title={`${device?.currentDevice?.model_type?.length > maxLength ? device?.currentDevice?.model_type : ''}`} arrow placement="top-start">
                  <div className="spec-value" id="lbl-currentdevice-model_type">
                    {handleShowDeviceInfo(device?.currentDevice?.model_type)}
                  </div>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </Card>
    </StyledCurrentDevice>
  );
}
